// =============================================================================
// MeuDriver — Batch P2: AdminPromoCodes
// Gestão de códigos promocionais (promo_codes) para admin.
// =============================================================================

import { useState } from 'react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Plus, Ticket, Pencil, Power, PowerOff, Loader2 } from 'lucide-react'
import { useForm, type SubmitHandler } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'

import {
  useAdminPromoCodes,
  useCreatePromoCode,
  useUpdatePromoCode,
  type PromoCode,
  type CreatePromoCodeInput,
} from '@/hooks/usePromoCodes'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { formatCurrency } from '@/lib/utils'

// ---------------------------------------------------------------------------
// Form schema
// ---------------------------------------------------------------------------

const promoSchema = z.object({
  code:           z.string().min(3).max(20).regex(/^[A-Za-z0-9_-]+$/, 'Apenas letras, números, _ e -'),
  discount_type:  z.enum(['percent', 'fixed']),
  discount_value: z.coerce.number().positive().max(100000),
  max_uses:       z.coerce.number().int().positive().nullable().optional(),
  valid_from:     z.string().optional(),
  valid_until:    z.string().nullable().optional(),
  is_active:      z.boolean().optional(),
})

type PromoFormValues = z.infer<typeof promoSchema>

// ---------------------------------------------------------------------------
// Modal de criação / edição
// ---------------------------------------------------------------------------

interface PromoModalProps {
  open: boolean
  onClose: () => void
  editing: PromoCode | null
}

function PromoModal({ open, onClose, editing }: PromoModalProps) {
  const create = useCreatePromoCode()
  const update = useUpdatePromoCode()
  const isPending = create.isPending || update.isPending

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<PromoFormValues>({
    resolver: zodResolver(promoSchema),
    defaultValues: editing
      ? {
          code:           editing.code,
          discount_type:  editing.discount_type,
          discount_value: editing.discount_value,
          max_uses:       editing.max_uses ?? undefined,
          valid_from:     editing.valid_from?.slice(0, 16) ?? '',
          valid_until:    editing.valid_until?.slice(0, 16) ?? '',
          is_active:      editing.is_active,
        }
      : {
          discount_type: 'percent',
          is_active:     true,
        },
  })

  const discountType = watch('discount_type')

  const onSubmit: SubmitHandler<PromoFormValues> = (values) => {
    const payload: CreatePromoCodeInput = {
      code:           values.code,
      discount_type:  values.discount_type,
      discount_value: values.discount_value,
      max_uses:       values.max_uses ?? null,
      valid_from:     values.valid_from ? new Date(values.valid_from).toISOString() : undefined,
      valid_until:    values.valid_until ? new Date(values.valid_until).toISOString() : null,
      is_active:      values.is_active ?? true,
    }

    const done = () => { reset(); onClose() }

    if (editing) {
      update.mutate({ id: editing.id, ...payload }, { onSuccess: done })
    } else {
      create.mutate(payload, { onSuccess: done })
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? 'Editar código' : 'Novo código promocional'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 pt-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5">
              <Label>Código</Label>
              <Input
                {...register('code')}
                placeholder="EX: VERAO20"
                className="uppercase"
                data-testid="promo-code-input"
              />
              {errors.code && <p className="text-xs text-red-500">{errors.code.message}</p>}
            </div>

            <div className="space-y-1.5">
              <Label>Tipo de desconto</Label>
              <Select
                defaultValue={editing?.discount_type ?? 'percent'}
                onValueChange={(v) => setValue('discount_type', v as 'percent' | 'fixed')}
              >
                <SelectTrigger data-testid="promo-discount-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Percentual (%)</SelectItem>
                  <SelectItem value="fixed">Valor fixo (R$)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Valor {discountType === 'percent' ? '(%)' : '(R$)'}</Label>
              <Input
                type="number"
                step="0.01"
                {...register('discount_value')}
                placeholder={discountType === 'percent' ? '10' : '25.00'}
                data-testid="promo-discount-value"
              />
              {errors.discount_value && (
                <p className="text-xs text-red-500">{errors.discount_value.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>Máx. usos (vazio = ilimitado)</Label>
              <Input
                type="number"
                min={1}
                {...register('max_uses')}
                placeholder="Ilimitado"
              />
            </div>

            <div className="space-y-1.5">
              <Label>Válido a partir de</Label>
              <Input type="datetime-local" {...register('valid_from')} />
            </div>

            <div className="col-span-2 space-y-1.5">
              <Label>Válido até (vazio = sem expiração)</Label>
              <Input type="datetime-local" {...register('valid_until')} />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isPending} data-testid="promo-save-btn">
              {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editing ? 'Salvar alterações' : 'Criar código'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Página principal
// ---------------------------------------------------------------------------

export default function AdminPromoCodes() {
  const { data: codes = [], isLoading } = useAdminPromoCodes()
  const updateCode = useUpdatePromoCode()

  const [modalOpen, setModalOpen]   = useState(false)
  const [editing, setEditing]       = useState<PromoCode | null>(null)

  const openCreate = () => { setEditing(null); setModalOpen(true) }
  const openEdit   = (c: PromoCode) => { setEditing(c); setModalOpen(true) }

  const toggleActive = (c: PromoCode) => {
    updateCode.mutate({ id: c.id, is_active: !c.is_active })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Códigos Promocionais</h1>
          <p className="text-sm text-gray-500 mt-1">Crie e gerencie descontos para os usuários da plataforma.</p>
        </div>
        <Button onClick={openCreate} className="gap-2" data-testid="create-promo-btn">
          <Plus className="h-4 w-4" />
          Novo código
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Ticket className="h-4 w-4 text-brand-600" />
            Todos os códigos
            {!isLoading && (
              <Badge variant="secondary" className="ml-auto">{codes.length}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : codes.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Ticket className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Nenhum código criado ainda.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50 dark:bg-gray-900/50">
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Código</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Desconto</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Usos</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Válido até</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Status</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {codes.map((code) => (
                    <tr key={code.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/30">
                      <td className="px-4 py-3 font-mono font-semibold text-brand-600">
                        {code.code}
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                        {code.discount_type === 'percent'
                          ? `${code.discount_value}%`
                          : formatCurrency(code.discount_value)}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                        {code.current_uses}
                        {code.max_uses != null ? ` / ${code.max_uses}` : ' / ∞'}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                        {code.valid_until
                          ? format(new Date(code.valid_until), "dd/MM/yy HH:mm", { locale: ptBR })
                          : 'Sem expiração'}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={code.is_active ? 'default' : 'secondary'}>
                          {code.is_active ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => openEdit(code)}
                            data-testid={`edit-promo-${code.id}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => toggleActive(code)}
                            title={code.is_active ? 'Desativar' : 'Ativar'}
                            data-testid={`toggle-promo-${code.id}`}
                          >
                            {code.is_active
                              ? <PowerOff className="h-3.5 w-3.5 text-red-500" />
                              : <Power className="h-3.5 w-3.5 text-green-500" />}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <PromoModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        editing={editing}
      />
    </div>
  )
}
